/**
 * #344 — the Game Detail order ticket's fee/cost economics, as pure functions.
 *
 * Every expression here is moved verbatim from `OrderEntryForm`; the comments
 * that explain WHY a formula is shaped the way it is came with them, because
 * they are the record of decisions that cost real money to get wrong.
 *
 * The one rule that governs the whole module: missing venue evidence stays
 * missing. An unknown fee is `null` and renders as an em dash — never a $0.00
 * that a trader could read as "free".
 */
import { formatMoney } from '@/lib/infra/formatters';
import { estimateAggregateFeeCents } from '@/lib/pricing/fees';
import { estimatePolyOrderFeeCents } from '@/lib/pricing/polyFees';
import type { PolyFeeSchedule } from '@/lib/polymarket/relay';
import type { SeriesFeeEntry } from '@/services/hydraFees';
import { americanOdds } from './tradeModalBook';
import { pricePresentation, type FeePerspective } from './ladder/helpers';
import type { LadderBuild, LadderSummary } from './orderLadder';
import { polyFeePreviewLabel, polyFeePreviewStatus, type PolyFeePreviewStatus } from './preTradeFeePreview';

export interface OrderEntryEconomicsInput {
  isKalshiVenue: boolean;
  orderType: 'limit' | 'market';
  /** Canonical decimal cents — the same value the submit path sends. */
  priceCents: number;
  count: number;
  /** Hydra series schedule; undefined while the initial fetch is in flight. */
  feeEntry: SeriesFeeEntry | undefined;
  /** 0..1. 0 unless the org is MM-enrolled and the rebate toggle is on. */
  makerRebateFraction: number;
  /** The dashboard's Raw/Fees toggle (`displaySettings.feeAdjustedOdds`).
   *  Governs {@link OrderEntryEconomics.priceQuote} ONLY — the money fields
   *  below are fee-inclusive in both states and always have been. */
  feeAdjusted: boolean;
  /** Which side of the book this ticket's price would land on, resolved by the
   *  caller from live depth. Drives the quote's fee rate; see `priceQuote`. */
  quotePerspective: FeePerspective;
  polyRole: 'maker' | 'taker' | null;
  polyConditionId: string | null;
  polyScheduleKnown: boolean;
  polySchedule: PolyFeeSchedule | null | undefined;
  polyFeeSchedulesLoading: boolean;
  polyFeeSchedulesError: string | null;
}

/** The Price row's cents and the American odds beside them, on ONE pricing
 *  basis (#452's contract, applied to the order ticket by #535).
 *
 *  `odds` is ALWAYS derived from `effectiveCents`, never from `rawCents` —
 *  that split is the whole defect #535 fixes. The two cents values are equal
 *  in Raw mode, on Polymarket (no hydra schedule), and wherever the applicable
 *  rate is 0; the renderer shows the pair only when they differ.
 *
 *  Neither value is a wire price. `rawCents` is what the submit path sends and
 *  is quantized purely for display. */
export interface OrderTicketQuote {
  rawCents: number;
  effectiveCents: number;
  /** `'—'` at the 0¢/100¢ boundaries, where no American line exists. */
  odds: string;
  perspective: FeePerspective;
}

export interface OrderEntryEconomics {
  isMaker: boolean;
  feeRate: number | null;
  cost: number;
  singleFillCents: number | null;
  makerRebateCents: number;
  netFeeCents: number | null;
  netFee: number | null;
  polyFeeStatus: PolyFeePreviewStatus;
  feeLabel: string;
  feeRole: 'maker' | 'taker' | null;
  feeTitle: string;
  totalCost: number | null;
  totalCostLabel: string;
  maxPayout: number;
  maxPayoutLabel: string;
  allInPriceCents: number | null;
  effectiveOddsLabel: string;
  maxProfit: number | null;
  maxProfitLabel: string;
  priceQuote: OrderTicketQuote;
}

export function computeOrderEntryEconomics(input: OrderEntryEconomicsInput): OrderEntryEconomics {
  const {
    isKalshiVenue,
    orderType,
    priceCents,
    count,
    feeEntry,
    makerRebateFraction,
    feeAdjusted,
    quotePerspective,
    polyRole,
    polyConditionId,
    polyScheduleKnown,
    polySchedule,
    polyFeeSchedulesLoading,
    polyFeeSchedulesError,
  } = input;

  const cost = (priceCents * count) / 100;

  // Kalshi uses the hydra series schedule. Polymarket uses the relay's
  // condition-id schedule, which is the same cache used to book realized
  // fill fees. A Poly LIMIT is maker only when its price is below the best
  // opposing bid-derived ask; a manually-entered marketable limit is taker.
  // Missing book depth or schedule stays unknown instead of becoming $0.
  const isMaker = orderType === 'limit';
  const feeRate = !feeEntry
    ? null
    : feeEntry.feeType === 'flat'
      ? null
      : isMaker
        ? (feeEntry.feeMakerRate ?? 0)
        : (feeEntry.feeTakerRate ?? 0);
  const singleFillCents =
    count === 0
      ? null
      : isKalshiVenue
        ? feeRate === null
          ? null
          : estimateAggregateFeeCents({ priceCents, count, feeRate })
        : polyRole == null
          ? null
          : estimatePolyOrderFeeCents({ priceCents, count, role: polyRole, schedule: polySchedule });
  // MM Program rebate: a fraction of the maker fee (50% pregame / 100%
  // live), applied only to maker (limit) orders. Reduces the displayed fee;
  // the rebate itself settles off-API with Kalshi. 0 when not active.
  const makerRebateCents =
    singleFillCents === null || !isKalshiVenue || !isMaker || makerRebateFraction <= 0
      ? 0
      : singleFillCents * Math.min(makerRebateFraction, 1);
  // No site-calibration on the pre-trade ticket: the aggregate per-fill ceil
  // already equals Kalshi's own ticket figure (it bakes in their 1¢ minimum),
  // and this preview must match what Kalshi advertises side-by-side.
  const netFeeCents = singleFillCents === null ? null : singleFillCents - makerRebateCents;
  const netFee = netFeeCents === null ? null : netFeeCents / 100;
  const polyFeeStatus = polyFeePreviewStatus({
    conditionId: polyConditionId,
    scheduleKnown: polyScheduleKnown,
    scheduleUnavailable: polyScheduleKnown && polySchedule === null,
    scheduleLoading: polyFeeSchedulesLoading,
    scheduleError: polyFeeSchedulesError,
    roleKnown: polyRole !== null,
    feeKnown: !isKalshiVenue && singleFillCents !== null,
  });
  const feeLabel =
    singleFillCents === null
      ? isKalshiVenue
        ? '—'
        : polyFeePreviewLabel(polyFeeStatus)
      : makerRebateCents > 0
        ? `${formatMoney(netFee ?? 0)} MMR`
        : formatMoney(netFee ?? 0);
  const feeRole = isKalshiVenue ? (isMaker ? 'maker' : 'taker') : polyRole;
  const feeTitle =
    feeRole === 'maker'
      ? 'Maker fee — applied only when the resting order fills'
      : feeRole === 'taker'
        ? 'Taker fee — applied immediately on fill'
        : 'Fee unavailable until the venue schedule and maker/taker role are known';
  // Total dollars out the door = contracts × price + total fee. This is
  // what the trader actually spends — drives both the Cost line and the
  // post-fee American-odds calc (payout $1 / cost-per-contract).
  const totalCost = netFee === null ? null : cost + netFee;
  const totalCostLabel = totalCost === null ? '—' : formatMoney(totalCost);
  // Each contract pays $1 on win — gross payout, not net of fee.
  const maxPayout = count;
  const maxPayoutLabel = formatMoney(maxPayout);
  // American odds priced off the all-in cost per contract: $1 payout
  // divided by (price + fee-per-contract).
  const allInPriceCents = totalCost === null || count === 0 ? null : (totalCost * 100) / count;
  const effectiveOddsLabel = allInPriceCents === null ? '—' : americanOdds(allInPriceCents);
  // The Price row's quote — a PER-CONTRACT pair, and a different quantity from
  // `effectiveOddsLabel` above: that one divides the whole order's all-in cost
  // (which carries Kalshi's per-fill ceil) by the quantity, so the two legiti-
  // mately diverge at small counts. #452 settled that split: order totals bill
  // the aggregate, a quote prices one contract at centicent precision.
  //
  // Routed through the LADDER's own `pricePresentation` on purpose. Agreement
  // with the dense row beside this ticket is the acceptance criterion, so it
  // has to be structural — two call sites of one function, not two functions
  // that happen to match today.
  //
  // Deliberately NOT `openOrderQuote` (components/cards/orderCardModel): that
  // one is maker-only, keyed on a bare rate rather than a SeriesFeeEntry, and
  // carries a resting-SELL fee direction this buy-only ticket has no use for.
  // Unifying them means giving it a taker perspective first.
  const presentation = pricePresentation(
    priceCents,
    quotePerspective,
    feeAdjusted,
    feeEntry,
    makerRebateFraction,
  );
  const priceQuote: OrderTicketQuote = {
    ...presentation,
    odds: americanOdds(presentation.effectiveCents),
  };
  // Max Profit = Max Payout − Total Cost. Null when the fee isn't loaded so we
  // don't fake a number that ignores it.
  const maxProfit = totalCost === null ? null : maxPayout - totalCost;
  const maxProfitLabel = maxProfit === null ? '—' : formatMoney(maxProfit, { showSign: true });

  return {
    isMaker,
    feeRate,
    cost,
    singleFillCents,
    makerRebateCents,
    netFeeCents,
    netFee,
    polyFeeStatus,
    feeLabel,
    feeRole,
    feeTitle,
    totalCost,
    totalCostLabel,
    maxPayout,
    maxPayoutLabel,
    allInPriceCents,
    effectiveOddsLabel,
    maxProfit,
    maxProfitLabel,
    priceQuote,
  };
}

export interface LadderTotals {
  contracts: number;
  legs: number;
  avgPriceCents: number;
  netFeeDollars: number | null;
  totalCostDollars: number | null;
  maxPayoutDollars: number;
  maxProfitDollars: number | null;
}

/**
 * Ladder aggregate for the summary card — the same per-rung fee model as the
 * single-order card (estimateAggregateFeeCents + MM rebate), summed across
 * valid rungs so Cost/Payout/Profit reconcile with placing them one by one.
 *
 * The rebate is applied PER RUNG, not once over the aggregate: each rung is its
 * own execution at the venue, so each one gets its own ceil-to-1¢ and its own
 * rebate off that ceiled figure.
 */
export function computeLadderTotals(args: {
  ladder: LadderBuild | null;
  ladderSummary: LadderSummary | null;
  feeRate: number | null;
  isMaker: boolean;
  makerRebateFraction: number;
}): LadderTotals | null {
  const { ladder, ladderSummary, feeRate, isMaker, makerRebateFraction } = args;
  if (!ladder || !ladderSummary) return null;
  let feeCents = 0;
  if (feeRate !== null) {
    for (const leg of ladder.valid) {
      const legFee = estimateAggregateFeeCents({
        priceCents: leg.priceCents,
        count: leg.qty,
        feeRate,
      });
      const rebate =
        isMaker && makerRebateFraction > 0 ? legFee * Math.min(makerRebateFraction, 1) : 0;
      feeCents += legFee - rebate;
    }
  }
  const notional = ladderSummary.notionalCents / 100;
  const netFeeDollars = feeRate === null ? null : feeCents / 100;
  const totalCostDollars = netFeeDollars === null ? null : notional + netFeeDollars;
  const maxPayoutDollars = ladderSummary.maxPayoutCents / 100;
  const maxProfitDollars = totalCostDollars === null ? null : maxPayoutDollars - totalCostDollars;
  return {
    contracts: ladderSummary.contracts,
    legs: ladderSummary.legs,
    avgPriceCents: ladderSummary.avgPriceCents,
    netFeeDollars,
    totalCostDollars,
    maxPayoutDollars,
    maxProfitDollars,
  };
}
